import React from 'react';
import { useNavigate } from 'react-router-dom';

export interface ErrorPageProps {
  /** 错误代码，默认为404 */
  code?: number;
  /** 错误标题，默认为"页面未找到" */
  title?: string;
  /** 错误描述，默认为"抱歉，您访问的页面不存在" */
  description?: string;
  /** 是否显示返回首页按钮，默认为true */
  showBackButton?: boolean;
  /** 自定义按钮文本，默认为"返回首页" */
  buttonText?: string;
  /** 自定义按钮点击处理函数 */
  onButtonClick?: () => void;
}

/**
 * 错误页面组件
 * 用于显示404等错误状态，提供美观的用户界面
 * 
 * @example
 * ```tsx
 * // 基本用法
 * <ErrorPage />
 * 
 * // 自定义错误信息
 * <ErrorPage 
 *   code={500}
 *   title="服务器错误"
 *   description="服务器暂时不可用，请稍后重试"
 * />
 * 
 * // 自定义按钮行为
 * <ErrorPage 
 *   buttonText="重新加载"
 *   onButtonClick={() => window.location.reload()}
 * />
 * ```
 */
export const ErrorPage: React.FC<ErrorPageProps> = ({
  code = 404,
  title = '页面未找到',
  description = '抱歉，您访问的页面不存在',
  showBackButton = true,
  buttonText = '返回首页',
  onButtonClick,
}) => {
  const navigate = useNavigate();

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br  font-sans">
      <div className="text-center p-8 bg-white/95 rounded-2xl shadow-2xl backdrop-blur-sm max-w-md w-11/12 mx-4">
        {/* 错误代码显示 */}
        <div className="text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600 mb-4 leading-none drop-shadow-lg">
          {code}
        </div>
        
        {/* 错误标题 */}
        <h1 className="text-3xl font-semibold text-gray-800 mb-2">
          {title}
        </h1>
        
        {/* 错误描述 */}
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          {description}
        </p>
        
        {/* 返回首页按钮 */}
        {showBackButton && (
          <button 
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold text-base cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={handleButtonClick}
          >
            {buttonText}
          </button>
        )}
        
        {/* 额外的帮助信息 */}
        <div className="mt-6 text-sm text-gray-500">
          <p>如果问题持续存在，请联系技术支持</p>
        </div>
      </div>
    </div>
  );
}; 